"use no memo";

import { useCallback, useEffect, useState, useTransition } from "react";

import { Autocomplete } from "@stacklet/ui";
import { Controller, useFormContext } from "react-hook-form";
import { graphql, useRefetchableFragment } from "react-relay";

import { useProvider } from "app/contexts/ProviderContext";
import {
  createRoleAssignmentChangeHandler,
  toAutocompleteValue,
  type RoleAssignmentComboItem,
} from "app/hooks/forms";
import { hasViewPermission } from "app/utils/rbac/permissions";

import type { PolicyCollectionPicker_policyCollections$key } from "./__generated__/PolicyCollectionPicker_policyCollections.graphql";
import type { PolicyCollectionPickerRefetchQuery } from "./__generated__/PolicyCollectionPickerRefetchQuery.graphql";

interface Props {
  /** The selected policy collection value */
  value?: string;
  /** Callback when policy collection selection changes */
  queryRef: PolicyCollectionPicker_policyCollections$key;
  /** Whether to allow multiple policy collection selection */
  multiple?: boolean;
  /** Whether the picker is disabled */
  disabled?: boolean;
  /** The name of the field to bind the picker to */
  fieldName?: string;
  /** Label for the picker */
  label?: string;
  /** Placeholder text for the input */
  placeholder?: string;
}

export function PolicyCollectionPicker({
  queryRef,
  disabled = false,
  multiple = false,
  fieldName = "policyCollection",
  label = "Policy collection *",
  placeholder = "Policy collection *",
}: Props) {
  const [, startTransition] = useTransition();
  const [searchValue, setSearchValue] = useState("");
  const { control } = useFormContext();
  const provider = useProvider();
  const [data, refetch] = useRefetchableFragment<
    PolicyCollectionPickerRefetchQuery,
    PolicyCollectionPicker_policyCollections$key
  >(
    graphql`
      fragment PolicyCollectionPicker_policyCollections on Query
      @argumentDefinitions(first: { type: "Int", defaultValue: 1000 })
      @refetchable(queryName: "PolicyCollectionPickerRefetchQuery") {
        policyCollections(
          filterElement: $policyCollectionFilterElement
          first: $first
        ) {
          edges {
            node {
              name
              uuid
              userPermissions
              roleAssignmentTarget
            }
          }
        }
      }
    `,
    queryRef,
  );

  const filteredEdges = (data?.policyCollections?.edges ?? []).filter((edge) =>
    hasViewPermission(edge.node.userPermissions),
  );

  const options: RoleAssignmentComboItem[] = filteredEdges.map((edge) => ({
    id: edge.node.uuid,
    name: edge.node.name,
    roleAssignmentTarget: edge.node.roleAssignmentTarget,
  }));

  const refetchPolicyCollections = useCallback(
    (search: string) => {
      const value = search.trim();

      if (value) {
        refetch({
          first: 1000,
          policyCollectionFilterElement: {
            multiple: {
              operator: "AND",
              operands: [
                {
                  single: {
                    name: "name",
                    value,
                  },
                },
                {
                  single: {
                    name: "provider",
                    value: provider,
                  },
                },
              ],
            },
          },
        });
      } else {
        refetch({
          first: 1000,
          policyCollectionFilterElement: {
            single: {
              name: "provider",
              value: provider,
            },
          },
        });
      }
    },
    [provider, refetch],
  );

  useEffect(() => {
    startTransition(() => {
      refetchPolicyCollections(searchValue);
    });
  }, [searchValue, refetchPolicyCollections]);

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({ field }) => (
        <Autocomplete
          className="my-xxs"
          inputRef={field.ref}
          isDisabled={disabled}
          items={options}
          label={label}
          menuTrigger="focus"
          multiple={multiple}
          onBlur={field.onBlur}
          onChange={createRoleAssignmentChangeHandler(field.onChange, multiple)}
          onInputChange={setSearchValue}
          placeholder={placeholder}
          tag={(item: RoleAssignmentComboItem) => (
            <Autocomplete.Tag id={item.id} textValue={item.name}>
              {item.name}
            </Autocomplete.Tag>
          )}
          value={toAutocompleteValue(field.value, multiple)}
        >
          {(item: RoleAssignmentComboItem) => (
            <Autocomplete.Option id={item.id} textValue={item.name}>
              <span>{item.name}</span>
            </Autocomplete.Option>
          )}
        </Autocomplete>
      )}
    />
  );
}
